import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pbAuthedFetch } from '../../authHeaders';
import { Icon, TitleIcon } from './icons';
import { RankBars, MicroBar, SummaryBand } from './viz';
import { useListControls, Pager, PAGE_SIZE_OPTIONS, SortTh } from './listControls';

// Admin-only "Users & groups" panel. Renders an add-user form, a users table,
// and a Groups sub-panel.

export const PAGE_LABELS: Record<string, string> = {
  overview: 'Overview',
  sources: 'Sources',
  registry: 'Registry',
  reconciliation: 'Reconciliation',
  assets: 'Assets',
  outages: 'Outages',
  licenses: 'Licenses',
  ato: 'ATO',
  gpo: 'GPO',
  discovery: 'Discovery',
  settings: 'Settings',
};
export const PAGE_IDS = Object.keys(PAGE_LABELS);

const LEVEL_DEFAULTS: Record<string, string[]> = {
  viewer: ['overview', 'sources', 'registry', 'reconciliation', 'assets', 'outages', 'gpo'],
};
LEVEL_DEFAULTS.member = [...LEVEL_DEFAULTS.viewer, 'licenses', 'ato', 'discovery'];
LEVEL_DEFAULTS.co = [...LEVEL_DEFAULTS.member];
LEVEL_DEFAULTS.pm = [...LEVEL_DEFAULTS.member, 'settings'];
LEVEL_DEFAULTS.admin = PAGE_IDS.slice();

function defaultPagesForLevel(level: string): string[] {
  const set = new Set(LEVEL_DEFAULTS[level] || LEVEL_DEFAULTS.viewer);
  return PAGE_IDS.filter((id) => set.has(id));
}

const LEVEL_OPTIONS = [
  { value: 'viewer', short: 'Viewer', label: 'Viewer (read-only)' },
  { value: 'member', short: 'Member', label: 'Member (can edit)' },
  { value: 'co', short: 'CO', label: 'CO (contracting officer)' },
  { value: 'pm', short: 'PM', label: 'PM (edit + delete)' },
  { value: 'admin', short: 'Admin', label: 'Admin (full access)' },
];
const LEVEL_VALUES = LEVEL_OPTIONS.map((o) => o.value);

const STATUS_META: Record<string, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'pill--ok' },
  invited: { label: 'Invited', cls: 'pill--warn' },
  disabled: { label: 'Disabled', cls: 'pill' },
};

interface User {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  group_id: string | null;
  allowed_pages: string[];
}

interface Group {
  id: string;
  name: string;
  role: string;
  allowed_pages: string[];
  member_count: number;
}

async function api(path: string, method = 'GET', body?: any) {
  const res = await pbAuthedFetch(path, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `request failed (${res.status})`);
    (err as any).status = res.status;
    (err as any).body = data;
    throw err;
  }
  return data;
}

function Req() {
  return <span aria-hidden="true" className="stg-req">*</span>;
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div className="stg-hint stg-hint-under">{children}</div>;
}

function ThSort({ label, sortKey, controls, num }: { label: string; sortKey: string; controls: ReturnType<typeof useListControls>; num?: boolean }) {
  const active = controls.sortKey === sortKey;
  const ariaSort = active ? (controls.sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" className={num ? 'th--num' : undefined}>
      <button type="button" className={num ? 'th th--num' : 'th'} onClick={() => controls.toggleSort(sortKey)} aria-sort={ariaSort}>
        {label}
      </button>
    </th>
  );
}

function PageChecks({ pages, onToggle, disabled }: { pages: Set<string>; onToggle: (id: string) => void; disabled: boolean }) {
  return (
    <div className={`stg-pagegrid${disabled ? ' is-disabled' : ''}`}>
      {PAGE_IDS.map((id) => (
        <label key={id} className="stg-check stg-check-dense">
          <input type="checkbox" checked={pages.has(id)} disabled={disabled} onChange={() => onToggle(id)} />
          {PAGE_LABELS[id]}
        </label>
      ))}
    </div>
  );
}

export default function UserAdmin() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [level, setLevel] = useState('member');
  const [groupId, setGroupId] = useState('');
  const [pages, setPages] = useState<Set<string>>(() => new Set(defaultPagesForLevel('member')));
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [gName, setGName] = useState('');
  const [gLevel, setGLevel] = useState('viewer');
  const [gPages, setGPages] = useState<Set<string>>(() => new Set(defaultPagesForLevel('viewer')));
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [restoreTo, setRestoreTo] = useState<string | null>(null);
  const delRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const { data: usersData } = useQuery<{ users: User[] }>({
    queryKey: ['admin-users'],
    queryFn: () => api('/api/admin/users'),
  });
  const { data: groupsData } = useQuery<{ groups: Group[] }>({
    queryKey: ['admin-groups'],
    queryFn: () => api('/api/admin/groups'),
  });

  const users: User[] = usersData?.users || [];
  const groups: Group[] = groupsData?.groups || [];

  useEffect(() => {
    if (!restoreTo) return;
    const el = delRefs.current[restoreTo];
    if (el) el.focus();
    setRestoreTo(null);
  }, [restoreTo]);

  useEffect(() => {
    if (!confirmId) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setConfirmId(null); setRestoreTo(confirmId); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmId]);

  const say = useCallback((ok: boolean, text: string) => { setMsgOk(ok); setMsg(text); }, []);

  const onLevel = (v: string) => { setLevel(v); setPages(new Set(defaultPagesForLevel(v))); };
  const togglePage = (id: string) => setPages((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const onGLevel = (v: string) => { setGLevel(v); setGPages(new Set(defaultPagesForLevel(v))); };
  const toggleGPage = (id: string) => setGPages((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const selectedGroup = groupId ? groups.find((g) => String(g.id) === String(groupId)) : null;
  const displayPages = selectedGroup ? new Set(selectedGroup.allowed_pages || []) : pages;

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const created = await api('/api/admin/users', 'POST', {
        email: email.trim(), firstName: first.trim(), lastName: last.trim(), level,
        pages: PAGE_IDS.filter((id) => pages.has(id)),
      });
      if (groupId) await api(`/api/admin/users/${created.user.id}/group`, 'PUT', { groupId });
      say(true, `Invite emailed to ${email.trim()}.`);
      setEmail(''); setFirst(''); setLast('');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (err: any) {
      if (err.body?.userId) {
        if (groupId) await api(`/api/admin/users/${err.body.userId}/group`, 'PUT', { groupId });
        say(false, `User created, but invite email failed.${groupId ? ' Group was applied.' : ''}`);
        setEmail(''); setFirst(''); setLast('');
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      } else {
        say(false, err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const addGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api('/api/admin/groups', 'POST', { name: gName.trim(), role: gLevel, pages: PAGE_IDS.filter((id) => gPages.has(id)) });
      setGName('');
      say(true, 'Group created.');
      queryClient.invalidateQueries({ queryKey: ['admin-groups'] });
    } catch (err: any) {
      say(false, err.message);
    }
  };

  const deleteGroup = (id: string, name: string) => {
    api(`/api/admin/groups/${id}`, 'DELETE')
      .then(() => { setConfirmId(null); say(true, `Group "${name}" deleted.`); queryClient.invalidateQueries({ queryKey: ['admin-groups'] }); })
      .catch((e) => say(false, e.message));
  };

  const cancelConfirm = (id: string) => { setConfirmId(null); setRestoreTo(id); };

  const setUserRole = (id: string, role: string) => api(`/api/admin/users/${id}/role`, 'PUT', { role }).then(() => queryClient.invalidateQueries({ queryKey: ['admin-users'] })).catch((e) => say(false, e.message));
  const setUserStatus = (id: string, status: string) => api(`/api/admin/users/${id}/status`, 'PUT', { status }).then(() => queryClient.invalidateQueries({ queryKey: ['admin-users'] })).catch((e) => say(false, e.message));
  const setUserGroup = (id: string, gid: string | null) => api(`/api/admin/users/${id}/group`, 'PUT', { groupId: gid }).then(() => queryClient.invalidateQueries({ queryKey: ['admin-users'] })).catch((e) => say(false, e.message));
  const resend = (id: string) => api(`/api/admin/users/${id}/resend-invite`, 'POST', {}).then(() => say(true, 'Invite re-sent.')).catch((e) => say(false, e.message));

  const levelMix = LEVEL_OPTIONS.map((o, i) => ({
    key: o.value,
    name: o.short,
    tone: `l${LEVEL_OPTIONS.length - i}`,
    value: users.filter((u) => (LEVEL_VALUES.includes(u.role) ? u.role : 'viewer') === o.value).length,
  }));
  const activeCount = users.filter((u) => (u.status || 'active') === 'active').length;

  const usersControls = useListControls(users, {
    searchText: (u) => `${u.display_name || ''} ${u.email || ''}`,
    sorters: {
      name: (u) => u.display_name || '',
      email: (u) => u.email || '',
      level: (u) => (LEVEL_VALUES.includes(u.role) ? u.role : 'viewer'),
      group: (u) => { const g = u.group_id ? groups.find((gg) => String(gg.id) === String(u.group_id)) : null; return g ? g.name : ''; },
      status: (u) => u.status || 'active',
      pages: (u) => (Array.isArray(u.allowed_pages) ? u.allowed_pages.length : PAGE_IDS.length),
    },
    filters: {
      level: (u, v) => (LEVEL_VALUES.includes(u.role) ? u.role : 'viewer') === v,
      status: (u, v) => (u.status || 'active') === v,
    },
    defaultPageSize: 10,
  });

  const groupsControls = useListControls(groups, {
    searchText: (g) => g.name || '',
    sorters: {
      name: (g) => g.name || '',
      level: (g) => g.role || '',
      pages: (g) => (Array.isArray(g.allowed_pages) ? g.allowed_pages.length : 0),
      members: (g) => Number(g.member_count) || 0,
    },
    filters: { level: (g, v) => (g.role || '') === v },
    defaultPageSize: 10,
  });

  return (
    <section aria-label="User management">
      <div className="stg-key"><Req /> indicates a required field.</div>

      {msg ? (
        <div className={`stg-notice${msgOk === false ? ' err' : msgOk === true ? ' ok' : ''}`} role="status" aria-live="polite">
          <span aria-hidden="true" className="stg-notice-d" />
          {msg}
        </div>
      ) : null}

      <section className="box">
        <header className="box__head">
          <TitleIcon name="userplus" />
          <div className="box__head-text"><h2 className="box__title">Add user</h2></div>
        </header>
        <div className="box__body">
          <form onSubmit={addUser} aria-busy={busy} className="stg-form">
            <div className="stg-row">
              <label className="stg-label" htmlFor="ua-email">Email<Req /></label>
              <input className="stg-input" type="email" id="ua-email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" placeholder="name@agency.gov" />
            </div>
            <div className="stg-grid2">
              <div className="stg-row">
                <label className="stg-label" htmlFor="ua-first">First name<Req /></label>
                <input className="stg-input" type="text" id="ua-first" required value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="off" />
              </div>
              <div className="stg-row">
                <label className="stg-label" htmlFor="ua-last">Last name<Req /></label>
                <input className="stg-input" type="text" id="ua-last" required value={last} onChange={(e) => setLast(e.target.value)} autoComplete="off" />
              </div>
            </div>
            <div className="stg-grid2" style={{ alignItems: 'start' }}>
              <div className="stg-row">
                <label className="stg-label" htmlFor="ua-level">Permission level</label>
                <select className="stg-input" id="ua-level" value={level} onChange={(e) => onLevel(e.target.value)} disabled={!!groupId}>
                  {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {groupId ? <Hint>Set by the selected group.</Hint> : <Hint>Governs edit / delete rights on shared data.</Hint>}
              </div>
              <div className="stg-row">
                <label className="stg-label" htmlFor="ua-group">Group (optional)</label>
                <select className="stg-input" id="ua-group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                  <option value="">— none (use checkboxes) —</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                <Hint>{groupId ? 'Level and page access come from this group.' : 'Pick a group to apply a saved access preset.'}</Hint>
              </div>
            </div>
            <fieldset className="stg-fieldset">
              <legend className="stg-label">{groupId ? 'Pages (from the selected group)' : 'Pages this user can access'}</legend>
              <PageChecks pages={displayPages} onToggle={togglePage} disabled={!!groupId} />
            </fieldset>
            <div className="stg-formfoot">
              <button type="submit" className="btn btn--primary" disabled={busy}>
                <Icon name="send" size={14} />{busy ? 'Sending invite…' : 'Add user & send invite'}
              </button>
            </div>
          </form>
        </div>
        <div className="box__foot">They get a temporary password by email and choose a new one on first sign-in.</div>
      </section>

      <section className="box">
        <header className="box__head">
          <TitleIcon name="users" />
          <div className="box__head-text"><h2 className="box__title">Users</h2></div>
        </header>
        {users.length ? (
          <SummaryBand inset eyebrow="Accounts" value={`${activeCount} of ${users.length} active`}>
            <RankBars label="Accounts by permission level" rows={levelMix} />
          </SummaryBand>
        ) : null}
        {users.length > 0 ? (
          <div className="box__toolbar">
            <div className="stg-filter">
              <label className="stg-label" htmlFor="ua-users-search">Search</label>
              <input className="stg-input pb-sel-sm" id="ua-users-search" type="search" value={usersControls.query} onChange={(e) => usersControls.onQuery(e.target.value)} placeholder="Name or email…" style={{ minWidth: '160px' }} />
            </div>
            <div className="stg-filter">
              <label className="stg-label" htmlFor="ua-users-filter-level">Level</label>
              <select className="stg-input pb-sel-sm" id="ua-users-filter-level" value={usersControls.filterValues.level || ''} onChange={(e) => usersControls.setFilter('level', e.target.value)}>
                <option value="">All</option>
                {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.short}</option>)}
              </select>
            </div>
            <div className="stg-filter">
              <label className="stg-label" htmlFor="ua-users-filter-status">Status</label>
              <select className="stg-input pb-sel-sm" id="ua-users-filter-status" value={usersControls.filterValues.status || ''} onChange={(e) => usersControls.setFilter('status', e.target.value)}>
                <option value="">All</option>
                {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
              </select>
            </div>
            <div className="stg-filter">
              <label className="stg-label" htmlFor="ua-users-pagesize">Show</label>
              <select className="stg-input pb-sel-sm" id="ua-users-pagesize" value={usersControls.pageSize} onChange={(e) => usersControls.setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                <option value="all">All</option>
              </select>
            </div>
            {usersControls.hasActiveFilters ? (
              <button type="button" className="btn btn--sm" onClick={usersControls.clearFilters}>Clear filters</button>
            ) : null}
          </div>
        ) : null}
        <div className={`box__body${usersControls.pageRows.length ? ' box__body--flush' : ''}`}>
          {users.length === 0 ? (
            <div className="state"><div className="state__title">No users yet</div><div className="state__body">Add one above to send the first invite.</div></div>
          ) : usersControls.filteredCount === 0 ? (
            <div className="state"><div className="state__title">No users match your filters</div><div className="state__body">Try a different search term, or clear the filters.</div></div>
          ) : (
            <div className="tbl-scroll">
              <table className="tbl">
                <caption className="pb-sr-only">Users — name, email, level, group, status, page count, and actions.</caption>
                <thead>
                  <tr>
                    <ThSort label="Name" sortKey="name" controls={usersControls} />
                    <ThSort label="Email" sortKey="email" controls={usersControls} />
                    <ThSort label="Level" sortKey="level" controls={usersControls} />
                    <ThSort label="Group" sortKey="group" controls={usersControls} />
                    <ThSort label="Status" sortKey="status" controls={usersControls} />
                    <ThSort label="Pages" sortKey="pages" controls={usersControls} />
                    <th scope="col"><span className="th">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {usersControls.pageRows.map((u) => {
                    const meta = STATUS_META[u.status] || { label: u.status || 'active', cls: 'pill' };
                    const pageCount = Array.isArray(u.allowed_pages) ? `${u.allowed_pages.length} / ${PAGE_IDS.length}` : 'All';
                    return (
                      <tr key={u.id}>
                        <td><span className="td--name">{u.display_name || '—'}</span></td>
                        <td><span className="td--id">{u.email || '—'}</span></td>
                        <td>
                          <select className="stg-input pb-sel-sm" value={LEVEL_VALUES.includes(u.role) ? u.role : 'viewer'} onChange={(e) => setUserRole(u.id, e.target.value)} aria-label={`Level for ${u.email}`}>
                            {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.short}</option>)}
                          </select>
                        </td>
                        <td>
                          <select className="stg-input pb-sel-sm" value={u.group_id || ''} onChange={(e) => setUserGroup(u.id, e.target.value || null)} aria-label={`Group for ${u.email}`}>
                            <option value="">—</option>
                            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </select>
                        </td>
                        <td><span className={`pill ${meta.cls}`}>{meta.label}</span></td>
                        <td>
                          <span className="stg-micro">
                            <MicroBar value={Array.isArray(u.allowed_pages) ? u.allowed_pages.length : PAGE_IDS.length} max={PAGE_IDS.length} />
                            <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums' }}>{pageCount}</span>
                          </span>
                        </td>
                        <td className="td--actions">
                          <div className="stg-rowbtns">
                            <button type="button" className="btn btn--sm" onClick={() => resend(u.id)}>Resend invite</button>
                            {u.status === 'disabled' ? (
                              <button type="button" className="btn btn--sm" onClick={() => setUserStatus(u.id, 'active')}>Enable</button>
                            ) : (
                              <button type="button" className="btn btn--sm stg-btn-quiet-danger" onClick={() => setUserStatus(u.id, 'disabled')}>Disable</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <Pager controls={usersControls} itemLabel="accounts" />
        <div className="box__foot">{users.length} account{users.length === 1 ? '' : 's'}</div>
      </section>

      <section className="box">
        <header className="box__head">
          <TitleIcon name="layers" />
          <div className="box__head-text"><h2 className="box__title">Groups</h2></div>
        </header>
        <div className="box__body">
          <form onSubmit={addGroup} className="stg-form stg-form-split">
            <div className="stg-grid2">
              <div className="stg-row">
                <label className="stg-label" htmlFor="ug-name">Group name<Req /></label>
                <input className="stg-input" type="text" id="ug-name" required value={gName} onChange={(e) => setGName(e.target.value)} placeholder="e.g. Field reviewers" autoComplete="off" />
              </div>
              <div className="stg-row">
                <label className="stg-label" htmlFor="ug-level">Level</label>
                <select className="stg-input" id="ug-level" value={gLevel} onChange={(e) => onGLevel(e.target.value)}>
                  {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.short}</option>)}
                </select>
              </div>
            </div>
            <fieldset className="stg-fieldset">
              <legend className="stg-label">Pages in this group</legend>
              <PageChecks pages={gPages} onToggle={toggleGPage} disabled={false} />
            </fieldset>
            <div className="stg-formfoot">
              <button type="submit" className="btn btn--primary"><Icon name="plus" size={14} />Create group</button>
            </div>
          </form>

          {groups.length === 0 ? (
            <div className="state"><div className="state__title">No groups yet</div><div className="state__body">Create one above to reuse an access preset.</div></div>
          ) : (
            <>
              <div className="box__toolbar">
                <div className="stg-filter">
                  <label className="stg-label" htmlFor="ua-groups-search">Search</label>
                  <input className="stg-input pb-sel-sm" id="ua-groups-search" type="search" value={groupsControls.query} onChange={(e) => groupsControls.onQuery(e.target.value)} placeholder="Group name…" style={{ minWidth: '160px' }} />
                </div>
                <div className="stg-filter">
                  <label className="stg-label" htmlFor="ua-groups-filter-level">Level</label>
                  <select className="stg-input pb-sel-sm" id="ua-groups-filter-level" value={groupsControls.filterValues.level || ''} onChange={(e) => groupsControls.setFilter('level', e.target.value)}>
                    <option value="">All</option>
                    {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.short}</option>)}
                  </select>
                </div>
              </div>
              {groupsControls.filteredCount === 0 ? (
                <div className="state"><div className="state__title">No groups match</div><div className="state__body">Try a different search term.</div></div>
              ) : (
                <div className="tbl-scroll">
                  <table className="tbl">
                    <caption className="pb-sr-only">Groups — name, level, page count, member count, and actions.</caption>
                    <thead>
                      <tr>
                        <ThSort label="Name" sortKey="name" controls={groupsControls} />
                        <ThSort label="Level" sortKey="level" controls={groupsControls} />
                        <ThSort label="Pages" sortKey="pages" controls={groupsControls} />
                        <ThSort label="Members" sortKey="members" controls={groupsControls} num />
                        <th scope="col"><span className="th">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupsControls.pageRows.map((g) => (
                        <tr key={g.id}>
                          <td><span className="td--name">{g.name || '—'}</span></td>
                          <td style={{ textTransform: 'capitalize' }}>{g.role || '—'}</td>
                          <td>
                            <span className="stg-micro">
                              <MicroBar value={Array.isArray(g.allowed_pages) ? g.allowed_pages.length : 0} max={PAGE_IDS.length} />
                              <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums' }}>
                                {Array.isArray(g.allowed_pages) ? `${g.allowed_pages.length} / ${PAGE_IDS.length}` : '—'}
                              </span>
                            </span>
                          </td>
                          <td className="td--num">{g.member_count != null ? g.member_count : '—'}</td>
                          <td className="td--actions">
                            {confirmId === g.id ? (
                              <div className="stg-confirm" role="group" aria-label={`Confirm deletion of ${g.name}`}>
                                <span className="stg-confirm-q">Delete {g.name}?</span>
                                <button type="button" className="btn btn--danger btn--sm" aria-label={`Yes, delete ${g.name}`} onClick={() => deleteGroup(g.id, g.name)}>Yes, delete</button>
                                <button type="button" className="btn btn--sm" onClick={() => cancelConfirm(g.id)}>Cancel</button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn--sm stg-btn-quiet-danger"
                                ref={(el) => { if (el) delRefs.current[g.id] = el; }}
                                aria-label={`Delete ${g.name}`}
                                onClick={() => setConfirmId(g.id)}
                              >Delete</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Pager controls={groupsControls} itemLabel="groups" />
            </>
          )}
        </div>
        <div className="box__foot">Named permission bundles — assign one to skip per-user checkboxes.</div>
      </section>
    </section>
  );
}
